import {
	DisplayContactModel
} from "client/Api/Email/contacts";
import {
	ComboBox,
	ComboBoxIntermediateState,
	Dialog,
	Form,
	FormItem,
	SuggestedComboBoxItem,
	Toolbar,
	useComboBoxIntermediateState,
	ValidationError
} from "client/Components";
import {
	AlertManager,
	createRemoteDataSource,
	useAlerts,
	useDataInputDialog,
	useDebouncedValue,
	useUnmountedCancellation
} from "client/Hooks";
import { getHighlight } from "client/Misc/highlight";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	AddContactCalendarResultModel,
	AddContactCalendarStatus,
	CalendarColor,
	CalendarInfoModel,
	addContactCalendar,
	findContactCalendars
} from "client/Api/Calendar/calendar";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { never } from "client_legacy/ndaw/errors";
import { generateTrackingId, isNil } from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { isNotNullNorUndefined } from "client/Validation/rules";
import { IFormHandler, useFormHandler } from "client/Forms";
import { RecipientListItem } from "../Email/EmailContent/EmailComposer/Header/RecipientListItem";

export type AddCalendarDialogProps = {
	calendarColor: CalendarColor;
	onAddCalendar: (newCalendar: CalendarInfoModel) => void;
	onClose: () => void;
};

export const AddCalendarDialog = (props: AddCalendarDialogProps): ReactElement => {
	const {
		calendarColor,
		onAddCalendar,
		onClose
	} = props;

	const [formModel, setFormModel] = useState<FormModel>({
		contactId: null
	});

	const [searchTerm, setSearchTerm] = useState<string | undefined>("");
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const debouncedSearchTerm: string | undefined = useDebouncedValue(searchTerm, 300);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManage: AlertManager = useAlerts();
	const contactIdState: ComboBoxIntermediateState<string> = useComboBoxIntermediateState<string>(undefined);

	const formHandler: IFormHandler<FormModel> = useFormHandler(
		formModel,
		setFormModel,
		validationConfiguration
	);

	const {
		currentItems,
		getLoadingState
	} = useContactsDataSource({
		searchTerm: debouncedSearchTerm ?? ""
	});

	const suggestedItems = useMemo(
		(): readonly SuggestedComboBoxItem<string, DisplayContactModel>[] => {
			if (!searchTerm) {
				return [];
			}

			return currentItems.map(i => ({
				value: i.id,
				text: i.emailAddress,
				highlight: getHighlight(i.emailAddress, searchTerm),
				data: i
			}));
		},
		[currentItems, searchTerm]
	);

	const validationErrorHandler = useCallback(
		(errorMessage: string): void => {
			formHandler
				.for("contactId")
				.validation
				.triggerInputFinished({
					controlValidation: {
						kind: InputValidationResultKind.Invalid,
						errorMessage
					}
				});
		},
		[formHandler]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			try {
				setIsBusy(true);

				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid
					|| !formModel.contactId
				) {
					setIsBusy(false);
					return;
				}

				const result: AddContactCalendarResultModel = await addContactCalendar(
					{
						contactId: formModel.contactId,
						color: calendarColor
					},
					globalCancellation
				);

				switch(result.status) {
					case AddContactCalendarStatus.AccessDenied:
						validationErrorHandler(localize("_AddContactCalendarPermissionErrorMessage"));
						break;
					case AddContactCalendarStatus.AlreadyExists:
						validationErrorHandler(localize("_ThisCalendarHasAlreadyBeenAdded"));
						break;
					case AddContactCalendarStatus.UnknownError:
						validationErrorHandler(localize("_CouldntAddThisContactYouMayNotHavePermissions"));
						break;
					case AddContactCalendarStatus.NoError:
						onAddCalendar(result.calendarInfo);
						alertManage.showSuccess(localize("_SuccessfullyAddedTheCalendar"));
						onClose();
						return;
					default:
						never(result);
				}

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManage.showError(localize("_GeneralErrorMessage"));
				console.error(e);
				setIsBusy(false);
			}
		},
		[
			alertManage,
			calendarColor,
			formHandler.validation,
			formModel.contactId,
			globalCancellation,
			onAddCalendar,
			onClose,
			validationErrorHandler
		]
	);

	return (
		<Dialog
			trackingId="add-calendar-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_AddCalendar")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit="NONE">
					<FormItem
						label={localize("_SelectFromDirectory")}
					>
						<ComboBox
							{...formHandler.for("contactId")
								.attachToComboBox(contactIdState, null)
							}
							searchTerm={searchTerm}
							onSearch={setSearchTerm}
							renderSuggestionBox={
								(props, ref) => (
									<ComboBox.SuggestionBox
										baseProps={props}
										suggestedItems={suggestedItems}
										isLoading={getLoadingState() === "loading" || searchTerm !== debouncedSearchTerm}
										menuTitle={localize("_SelectFromDirectory")}
										emptySearchTermBehavior="show-hint"
										ref={ref}
									>
										{(item, onClick): ReactElement => (
											<RecipientListItem
												trackingId={generateTrackingId("contact", `|${item.value}`)}
												item={item.data}
												highlights={{ emailAddress: item.highlight }}
												onClick={onClick}
											/>
										)}
									</ComboBox.SuggestionBox>
								)
							}
							placeholder={localize("_SearchForAContactOrCalendar")}
						/>
						<ValidationError {...formHandler.for("contactId").attachToValidationError()}/>
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						trackingId="add-contact"
						kind="primary"
						text={localize("_Add")}
						onClick={submitHandler}
						isDisabled={isNil(formModel.contactId)}
					/>
					<Toolbar.Button
						trackingId="cancel"
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
AddCalendarDialog.displayName = "AddCalendarDialog";

type FormModel = {
	contactId: string | null;
};

const validationConfiguration: IValidationConfiguration<FormModel> = createValidationConfiguration<FormModel>();

validationConfiguration.forScalar("contactId")
	.add(
		isNotNullNorUndefined()
	);

const useContactsDataSource = createRemoteDataSource<
	DisplayContactModel
>()
	.setConfiguration({
		hasSearch: true
	})
	.getHook(
		{
			performRequest: async (options): Promise<readonly DisplayContactModel[]> => {
				const {
					searchTerm,
					signal
				} = options;

				if (searchTerm === "") {
					return [];
				}

				return await findContactCalendars(
					searchTerm,
					signal
				);
			}
		}
	);

type AddCalendarDialogInitialData = Omit<AddCalendarDialogProps,
	| "onClose"
	| "onAddCalendar"
>;

export function useComposeAppointmentDialog(): [
	element: ReactElement | null,
	open: (props: AddCalendarDialogInitialData) => Promise<TryResult<CalendarInfoModel>>
] {
	return useDataInputDialog(
		(props, onClose, onAddCalendar) => {
			return (
				<AddCalendarDialog
					{...props}
					onClose={onClose}
					onAddCalendar={onAddCalendar}
				/>
			);
		}
	);
}